import {Injectable} from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivate,
  CanLoad,
  Route,
  Router,
  RouterStateSnapshot,
  UrlSegment,
  UrlTree
} from '@angular/router';
import {Observable, throwError} from 'rxjs';
import {catchError, map, take} from 'rxjs/operators';
import {AuthService} from './auth.service';
import {ApiService} from './api.service';
import {CommonUtils} from "../utils/common";

/**
 * Strażnik routingu (Guard) zarządzający kontrolą dostępu do modułu Planów Opieki
 * (Wellness Plans).
 * Weryfikuje po stronie serwera API, czy zalogowany użytkownik posiada uprawnienia
 * (RBAC) do przeglądania i konfiguracji pakietów profilaktycznych w ramach placówki.
 */
@Injectable({
  providedIn: 'root'
})
export class WellnessPlansAccessGuard implements CanLoad, CanActivate {
  constructor(
    private authService: AuthService,
    private router: Router,
    private apiService: ApiService,
    public commonUtils: CommonUtils
  ) {
  }

  /**
   * Blokuje lub zezwala na asynchroniczne pobranie (Lazy Loading) kodu JavaScript
   * modułu planów opieki. Optymalizuje bezpieczeństwo aplikacji poprzez wymuszenie
   * walidacji na dedykowanym endpointcie API przed zainicjowaniem transferu logiki biznesowej.
   *
   * @param route Konfiguracja chronionej ścieżki.
   * @param segments Segmenty adresu URL.
   * @returns Observable emitujący wartość logiczną (true w przypadku pomyślnej autoryzacji).
   */
  canLoad(
    route: Route,
    segments: UrlSegment[]
  ): Observable<boolean> | Promise<boolean> | boolean {
    return this.apiService.post('/dashboard/wellness-plans-access', {}).pipe(take(1), catchError(err => {
      if (err.status === 401) {
        this.router.navigate(['signin']).then(() => {
          this.authService.logout();
        });
      }
      return throwError(err);
    }), map(response => {
      return true;
    }));
  }

  /**
   * Weryfikuje uprawnienia w momencie fizycznej aktywacji interfejsu panelu planów opieki.
   * Chroni spójność sesji aplikacji poprzez przechwytywanie błędów wygaśnięcia tokena (HTTP 401)
   * i wymusza bezpieczne wylogowanie. Weryfikację ewentualnej odmowy dostępu
   * deleguje do ustandaryzowanej metody z klasy CommonUtils.
   *
   * @param route Migawka aktywowanej ścieżki.
   * @param state Migawka stanu routera.
   * @returns Observable emitujący wartość logiczną lub bezpieczne drzewo routingu (UrlTree).
   */
  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    return this.apiService.post('/dashboard/wellness-plans-access', {}).pipe(take(1), catchError(err => {
      if (err.status === 401) {
        this.router.navigate(['signin']).then(() => {
          this.authService.logout();
        });
      }
      return throwError(err);
    }), map(response => {
      return this.commonUtils.handleHttpResponseShowInfoToast(response, '', 'Nie masz uprawnień do tej strony')
    }));
  }

}
